import { useEffect, useRef } from 'react';

// Schedule after completion, never on a fixed interval. Callback updates do not
// restart a pending request or reset the retry delay.
export default function useNotificationPolling(url, userId, onData) {
  const callback = useRef(onData);
  callback.current = onData;
  useEffect(() => {
    if (!userId) return undefined;
    let stopped = false;
    let running = false;
    let timer;
    let controller;
    let failures = 0;
    let nextAt = 0;
    const schedule = (delay) => {
      clearTimeout(timer);
      if (!stopped && !document.hidden) timer = setTimeout(run, delay);
    };
    const run = async () => {
      if (stopped || running || document.hidden) return;
      if (Date.now() < nextAt) { schedule(nextAt - Date.now()); return; }
      running = true;
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!stopped) await callback.current(Array.isArray(data) ? data : []);
        failures = 0;
      } catch (error) {
        failures += 1;
        if (!stopped) console.warn('Notification refresh delayed:', error.message);
      } finally {
        clearTimeout(timeout);
        running = false;
        const delay = failures ? Math.min(120000, 15000 * (2 ** Math.min(failures, 3))) : 15000;
        nextAt = Date.now() + delay + Math.floor(Math.random() * 1000);
        schedule(nextAt - Date.now());
      }
    };
    const visible = () => {
      if (document.hidden) clearTimeout(timer);
      else schedule(Math.max(0, nextAt - Date.now()));
    };
    document.addEventListener('visibilitychange', visible);
    schedule(0);
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener('visibilitychange', visible);
    };
  }, [url, userId]);
}
